'use client'
import { ReactNode, forwardRef, useEffect, useState, ElementType } from 'react'
import classNames from 'classnames'

import { BoxProps } from 'shared/components/Box/Box'
import { useInputField } from 'shared/components/Form/InputField'
import { InputHelperText } from './InputHelperText'

export type InputProps = Omit<BoxProps<'input'>, 'value' | 'id'> & {
  icon?: ReactNode
  variant?: 'outlined' | 'ghost'
  hidden?: boolean
  as?: ElementType
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ icon, className, variant = 'outlined', hidden, as, ...props }, ref) => {
    const { id } = useInputField()
    const [message, setMessage] = useState('')

    useEffect(() => {
      const inputEl = document.getElementById(id) as HTMLInputElement | null
      if (!inputEl) return

      inputEl.setAttribute('aria-describedby', `error-text-${id}`)
      const fieldEl = inputEl.closest('.form-field')

      const onBlur = (e: Event) => {
        e.preventDefault()
        setMessage(inputEl.validationMessage)
        if (fieldEl && !inputEl.checkValidity()) {
          inputEl.setAttribute('aria-invalid', 'true')
          fieldEl.setAttribute('data-status', 'error')
        }
      }
      const onInput = () => {
        if (fieldEl && inputEl.checkValidity()) {
          setMessage('')
          inputEl.setAttribute('aria-invalid', 'false')
          fieldEl.setAttribute('data-status', 'success')
        }
      }

      inputEl.addEventListener('blur', onBlur)
      inputEl.addEventListener('input', onInput)
      return () => {
        inputEl.removeEventListener('blur', onBlur)
        inputEl.removeEventListener('input', onInput)
      }
    }, [id])

    const El = as ?? 'input'
    if (hidden) {
      return (
        <El
          ref={ref}
          {...props}
          id={id}
          aria-describedby={`error-text-${id}`}
          hidden
        />
      )
    }

    return (
      <>
        <div
          className={classNames('form-input-wrapper', className)}
          data-variant={variant}
        >
          {!!icon && icon}
          <El
            ref={ref}
            {...props}
            id={id}
            aria-describedby={`error-text-${id}`}
          />
        </div>
        {message && (
          <InputHelperText className="form-error-text" id={`error-text-${id}`}>
            {message}
          </InputHelperText>
        )}
      </>
    )
  }
)

Input.displayName = 'Input'
export default Input
